'use client';

import { fetchFormatCategoriesList, fetchProgramCategoriesList } from '@/lib/api/webapp/categories';
import { type CategoryDropdownMenu, type CategoryOption } from '@/types/categories';
import { useLocale, useTranslations } from 'next-intl';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useEffect, useReducer, useRef } from 'react';
import { CategoriesAction, CategoriesState, MatchMode } from './interfaces';

const categoriesInitialState: CategoriesState = {
    categoriesProgram: [],
    categoriesFormat: [],
    categoriesDashboard: [],
    isLoaded: false,
};

/**
 * Each SET_CATEGORIES_* action replaces the corresponding array and marks the state as loaded.
 * RESET_FOR_LOCALE_CHANGE empties everything so the fetch effect re-triggers.
 */
const categoriesReducer = (state: CategoriesState, action: CategoriesAction): CategoriesState => {
    switch (action.type) {
        case 'SET_CATEGORIES_PROGRAM':
            return { ...state, categoriesProgram: action.payload };
        case 'SET_CATEGORIES_FORMAT':
            return { ...state, categoriesFormat: action.payload, isLoaded: true };
        case 'SET_CATEGORIES_DASHBOARD':
            return { ...state, categoriesDashboard: action.payload };
        case 'RESET_FOR_LOCALE_CHANGE':
            return { ...categoriesInitialState };
        default:
            return state;
    }
};

const segmentsFromPath = (href: string): string[] =>
    href
        .split('/')
        .filter(Boolean)
        .map((s) => s.toLowerCase());

type UseControllerParams = {
    translateNavigation: (key: string) => string;
};

export const useController = ({ translateNavigation }: UseControllerParams) => {
    const locale = useLocale().toLowerCase();

    const [categoriesState, dispatch] = useReducer(categoriesReducer, categoriesInitialState);

    const previousLocaleRef = useRef(locale);

    /** When the locale changes, reset the categories so the fetch effect re-runs */
    useEffect(() => {
        if (previousLocaleRef.current !== locale) {
            previousLocaleRef.current = locale;
            dispatch({ type: 'RESET_FOR_LOCALE_CHANGE' });
        }
    }, [locale]);

    const segments = useSelectedLayoutSegments();
    const currentSegments = Array.isArray(segments) ? segments.filter(Boolean).map((s) => s.toLowerCase()) : [];

    const isLinkActive = (href: string, matchMode: MatchMode = 'exact'): boolean => {
        const hrefSegments = segmentsFromPath(href ?? '/');

        if (matchMode === 'exact') {
            if (hrefSegments.length !== currentSegments.length) return false;
            return hrefSegments.every((segment, index) => segment === currentSegments[index]);
        }

        if (hrefSegments.length === 0 && currentSegments.length === 0) return true;
        if (hrefSegments.length === 0 || hrefSegments.length > currentSegments.length) return false;
        return hrefSegments.every((segment, index) => segment === currentSegments[index]);
    };

    /** Fetches program and format categories, then marks the state as loaded */
    useEffect(() => {
        if (categoriesState.isLoaded) return;

        const fetchCategories = async () => {
            const programCategoriesResponse = await fetchProgramCategoriesList();

            const hardcodedGenreIds: Record<string, string> = process.env.NEXT_PUBLIC_GENRE_CATALOG_MENU_IDS
                ? JSON.parse(process.env.NEXT_PUBLIC_GENRE_CATALOG_MENU_IDS)
                : {};

            const programCategories: CategoryDropdownMenu[] = [
                ...programCategoriesResponse.data,
                ...Object.keys(hardcodedGenreIds).map((key) => {
                    const genreKey = `buyer.programs.genreCatalogMenuItems.${hardcodedGenreIds[key].toLowerCase()}`;
                    return {
                        id: key,
                        title: translateNavigation(genreKey),
                        isAnExcludedGenre: true,
                    };
                }),
            ].sort((a, b) => (a.title || '').localeCompare(b.title || ''));

            dispatch({ type: 'SET_CATEGORIES_PROGRAM', payload: programCategories });

            const formatCategoriesResponse = await fetchFormatCategoriesList();

            const formatCategories = formatCategoriesResponse.data.sort((a: CategoryOption, b: CategoryOption) =>
                (a.title || '').localeCompare(b.title || ''),
            );

            dispatch({ type: 'SET_CATEGORIES_FORMAT', payload: formatCategories });
        };

        fetchCategories();
    }, [categoriesState.isLoaded, translateNavigation]);

    return {
        categoriesProgram: categoriesState.categoriesProgram,
        categoriesFormat: categoriesState.categoriesFormat,
        categoriesDashboard: categoriesState.categoriesDashboard,
        isLinkActive,
    };
};
